import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import NewsCard from "@/components/base/NewsCard";
import {
  MOVIE_GENRE_OPTIONS,
  TV_GENRE_OPTIONS,
  REGION_OPTIONS,
  YEAR_OPTIONS,
  SORT_OPTIONS,
  getMediaListByType,
  newsList,
} from "@/lib/catalog";
import type { MediaItem, NewsItem } from "@/types/media";

type CategoryType = "movie" | "tv" | "news";

const metaMap: Record<CategoryType, { title: string; desc: string; label: string }> = {
  movie: {
    title: "最新电影推荐",
    desc: "最新电影资源及推荐，涵盖科幻、动作、悬疑、剧情等多种类型影片供您选择。",
    label: "电影",
  },
  tv: {
    title: "热门电视剧",
    desc: "热门电视剧资源汇总，涵盖历史、都市、悬疑、科幻等多题材剧集，追剧不迷路。",
    label: "电视剧",
  },
  news: {
    title: "影视资讯",
    desc: "最新影视资讯报道，涵盖行业动态、剧集资讯、人物专访与经典回顾。",
    label: "资讯",
  },
};

const PAGE_SIZE_MEDIA = 8;
const PAGE_SIZE_NEWS = 6;

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}

function FilterGroup({ label, options, value, onChange }: FilterGroupProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0 pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value === opt
                ? "bg-secondary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const categoryType: CategoryType =
    type === "tv" || type === "news" ? (type as CategoryType) : "movie";
  const meta = metaMap[categoryType];

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [newsSort, setNewsSort] = useState("updated");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("updated");
    setNewsSort("updated");
    setPage(1);
    setFilterOpen(false);
  }, [categoryType]);

  useEffect(() => {
    document.title = `${meta.label} - 麻豆网站`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", meta.desc);
  }, [meta]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort, newsSort]);

  const genreOptions = categoryType === "tv" ? TV_GENRE_OPTIONS : MOVIE_GENRE_OPTIONS;

  const mediaResult = useMemo(() => {
    if (categoryType === "news") return [] as MediaItem[];
    let list = getMediaListByType(categoryType);
    if (genre !== "全部") list = list.filter((i) => i.genres.includes(genre));
    if (region !== "全部") list = list.filter((i) => i.region === region);
    if (year !== "全部") list = list.filter((i) => String(i.year) === year);
    const sorted = [...list];
    if (sort === "popularity") sorted.sort((a, b) => b.popularity - a.popularity);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return sorted;
  }, [categoryType, genre, region, year, sort]);

  const newsResult = useMemo(() => {
    if (categoryType !== "news") return [] as NewsItem[];
    const sorted = [...newsList];
    if (newsSort === "related") sorted.sort((a, b) => a.title.localeCompare(b.title));
    else sorted.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    return sorted;
  }, [categoryType, newsSort]);

  const isNews = categoryType === "news";
  const pageSize = isNews ? PAGE_SIZE_NEWS : PAGE_SIZE_MEDIA;
  const total = isNews ? newsResult.length : mediaResult.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentMedia = mediaResult.slice((page - 1) * pageSize, page * pageSize);
  const currentNews = newsResult.slice((page - 1) * pageSize, page * pageSize);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("updated");
    setNewsSort("updated");
  };

  const goPage = (p: number) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      {/* 面包屑 */}
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
        <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800 font-medium">{meta.label}</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{meta.title}</h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl">{meta.desc}</p>
      </header>

      {/* 筛选 + 排序 */}
      {!isNews ? (
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 mb-6">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="lg:hidden w-full flex items-center justify-between text-sm font-medium text-foreground-800 mb-1"
          >
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-filter-3-line text-primary-500" />
              筛选条件
            </span>
            <i className={filterOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"} />
          </button>
          <div className={`${filterOpen ? "block" : "hidden"} lg:block mt-3`}>
            <FilterGroup label="类型" options={genreOptions} value={genre} onChange={setGenre} />
            <FilterGroup label="地区" options={REGION_OPTIONS} value={region} onChange={setRegion} />
            <FilterGroup label="年份" options={YEAR_OPTIONS} value={year} onChange={setYear} />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-4 mt-1">
            <span className="text-sm font-medium text-foreground-500 mr-1">排序</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSort(opt.value)}
                className={`px-3 py-1 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === opt.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-sm font-medium text-foreground-500 mr-1">排序</span>
          {[
            { value: "updated", label: "按时间" },
            { value: "related", label: "按话题" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setNewsSort(opt.value)}
              className={`px-3 py-1 rounded-md text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                newsSort === opt.value
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* 结果统计 */}
      <p className="text-xs text-foreground-500 mb-4">共找到 {total} 条内容</p>

      {/* 列表 */}
      {total === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200 rounded-lg">
          <i className="ri-inbox-2-line text-5xl text-foreground-300 mb-4" />
          <p className="text-base font-medium text-foreground-700 mb-1">抱歉，暂无相关内容</p>
          <p className="text-sm text-foreground-500 mb-5">试试更换筛选条件，或重置后浏览全部内容</p>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      ) : (
        <>
          {isNews ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {currentNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {currentMedia.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-10 flex-wrap">
              <button
                type="button"
                onClick={() => goPage(page - 1)}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    p === page
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goPage(page + 1)}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}